// Adapted from Alien Signals
// https://github.com/stackblitz/alien-signals/

const SubscriberFlagsComputed = 1 << 0;
const SubscriberFlagsEffect = 1 << 1;
const SubscriberFlagsTracking = 1 << 2;
const SubscriberFlagsNotified = 1 << 3;
const SubscriberFlagsRecursed = 1 << 4;
const SubscriberFlagsDirty = 1 << 5;
const SubscriberFlagsPendingComputed = 1 << 6;
const SubscriberFlagsPendingEffect = 1 << 7;
const SubscriberFlagsPropagated = SubscriberFlagsDirty | SubscriberFlagsPendingComputed | SubscriberFlagsPendingEffect;
function createReactiveSystem({
  updateComputed,
  notifyEffect
}) {
  const notifyBuffer = [];
  let notifyIndex = 0;
  let notifyBufferLength = 0;
  return [link, propagate, updateDirtyFlag, startTracking, endTracking, processEffectNotifications, processComputedUpdate, processPendingInnerEffects];

  /**
   * Links a given dependency and subscriber if they are not already linked.
   *
   * @param dep - The dependency to be linked.
   * @param sub - The subscriber that depends on this dependency.
   * @returns The newly created link object if the two are not already linked;
   *   otherwise `undefined`.
   */
  function link(dep, sub) {
    const currentDep = sub._depsTail;
    if (currentDep?.dep === dep) {
      return;
    }
    const nextDep = currentDep !== undefined ? currentDep.nextDep : sub._deps;
    if (nextDep?.dep === dep) {
      sub._depsTail = nextDep;
      return;
    }
    const depLastSub = dep._subsTail;
    if (depLastSub?.sub === sub && isValidLink(depLastSub, sub)) {
      return;
    }
    return linkNewDep(dep, sub, nextDep, currentDep);
  }

  /**
   * Traverses and marks subscribers starting from the provided link.
   *
   * It sets flags (e.g., Dirty, PendingComputed, PendingEffect) on each
   * subscriber to indicate which ones require re-computation or effect
   * processing. This function should be called after a signal's value changes.
   *
   * @param current - The starting link from which propagation begins.
   */
  function propagate(current) {
    let next = current.nextSub;
    let branchs;
    let branchDepth = 0;
    let targetFlag = SubscriberFlagsDirty;
    top: do {
      const sub = current.sub;
      const subFlags = sub._flags;
      let shouldNotify = false;
      if (!(subFlags & (SubscriberFlagsTracking | SubscriberFlagsRecursed | SubscriberFlagsPropagated))) {
        sub._flags = subFlags | targetFlag | SubscriberFlagsNotified;
        shouldNotify = true;
      } else if (subFlags & SubscriberFlagsRecursed && !(subFlags & SubscriberFlagsTracking)) {
        sub._flags = subFlags & ~SubscriberFlagsRecursed | targetFlag | SubscriberFlagsNotified;
        shouldNotify = true;
      } else if (!(subFlags & SubscriberFlagsPropagated) && isValidLink(current, sub)) {
        sub._flags = subFlags | SubscriberFlagsRecursed | targetFlag | SubscriberFlagsNotified;
        shouldNotify = sub._subs !== undefined;
      }
      if (shouldNotify) {
        const subSubs = sub._subs;
        if (subSubs !== undefined) {
          current = subSubs;
          if (subSubs.nextSub !== undefined) {
            branchs = {
              target: next,
              linked: branchs
            };
            ++branchDepth;
            next = current.nextSub;
            targetFlag = SubscriberFlagsPendingComputed;
          } else {
            targetFlag = subFlags & SubscriberFlagsEffect ? SubscriberFlagsPendingEffect : SubscriberFlagsPendingComputed;
          }
          continue;
        }
        if (subFlags & SubscriberFlagsEffect) {
          notifyBuffer[notifyBufferLength++] = sub;
        }
      } else if (!(subFlags & (SubscriberFlagsTracking | targetFlag))) {
        sub._flags = subFlags | targetFlag | SubscriberFlagsNotified;
        if ((subFlags & (SubscriberFlagsEffect | SubscriberFlagsNotified)) === SubscriberFlagsEffect) {
          notifyBuffer[notifyBufferLength++] = sub;
        }
      } else if (!(subFlags & targetFlag) && subFlags & SubscriberFlagsPropagated && isValidLink(current, sub)) {
        sub._flags = subFlags | targetFlag;
      }
      if ((current = next) !== undefined) {
        next = current.nextSub;
        targetFlag = branchDepth ? SubscriberFlagsPendingComputed : SubscriberFlagsDirty;
        continue;
      }
      while (branchDepth--) {
        current = branchs.target;
        branchs = branchs.linked;
        if (current !== undefined) {
          next = current.nextSub;
          targetFlag = branchDepth ? SubscriberFlagsPendingComputed : SubscriberFlagsDirty;
          continue top;
        }
      }
      break;
      // eslint-disable-next-line
    } while (true);
  }

  /**
   * Prepares the given subscriber to track new dependencies.
   *
   * It resets the subscriber's internal pointers (e.g., depsTail) and sets its
   * flags to indicate it is now tracking dependency links.
   *
   * @param sub - The subscriber to start tracking.
   */
  function startTracking(sub) {
    sub._depsTail = undefined;
    sub._flags = sub._flags & ~(SubscriberFlagsNotified | SubscriberFlagsRecursed | SubscriberFlagsPropagated) | SubscriberFlagsTracking;
  }

  /**
   * Concludes tracking of dependencies for the specified subscriber.
   *
   * It clears or unlinks any tracked dependency information, then updates the
   * subscriber's flags to indicate tracking is complete.
   *
   * @param sub - The subscriber whose tracking is ending.
   */
  function endTracking(sub) {
    const depsTail = sub._depsTail;
    if (depsTail !== undefined) {
      const nextDep = depsTail.nextDep;
      if (nextDep !== undefined) {
        clearTracking(nextDep);
        depsTail.nextDep = undefined;
      }
    } else if (sub._deps !== undefined) {
      clearTracking(sub._deps);
      sub._deps = undefined;
    }
    sub._flags &= ~SubscriberFlagsTracking;
  }

  /**
   * Updates the dirty flag for the given subscriber based on its dependencies.
   *
   * If the subscriber has any pending computeds, this function sets the Dirty
   * flag and returns `true`. Otherwise, it clears the PendingComputed flag and
   * returns `false`.
   *
   * @param sub - The subscriber to update.
   * @param flags - The current flag set for this subscriber.
   * @returns `true` if the subscriber is marked as Dirty; otherwise `false`.
   */
  function updateDirtyFlag(sub, flags) {
    if (checkDirty(sub._deps)) {
      sub._flags = flags | SubscriberFlagsDirty;
      return true;
    } else {
      sub._flags = flags & ~SubscriberFlagsPendingComputed;
      return false;
    }
  }

  /**
   * Updates the computed subscriber if necessary before its value is accessed.
   *
   * If the subscriber is marked Dirty or PendingComputed, this function runs
   * the provided updateComputed logic and triggers a shallowPropagate for any
   * downstream subscribers if an actual update occurs.
   *
   * @param computed - The computed subscriber to update.
   * @param flags - The current flag set for this subscriber.
   */
  function processComputedUpdate(computed, flags) {
    if (flags & SubscriberFlagsDirty || checkDirty(computed._deps)) {
      if (updateComputed(computed)) {
        const subs = computed._subs;
        if (subs !== undefined) {
          shallowPropagate(subs);
        }
      }
    } else {
      computed._flags = flags & ~SubscriberFlagsPendingComputed;
    }
  }

  /**
   * Ensures all pending internal effects for the given subscriber are
   * processed.
   *
   * This should be called after an effect decides not to re-run itself but may
   * still have dependencies flagged with PendingEffect. If the subscriber is
   * flagged with PendingEffect, this function clears that flag and invokes
   * `notifyEffect` on any related dependencies marked as Effect and Propagated,
   * processing pending effects.
   *
   * @param sub - The subscriber which may have pending effects.
   * @param flags - The current flags on the subscriber to check.
   */
  function processPendingInnerEffects(sub, flags) {
    if (flags & SubscriberFlagsPendingEffect) {
      sub._flags = flags & ~SubscriberFlagsPendingEffect;
      let link = sub._deps;
      do {
        const dep = link.dep;
        if ('_flags' in dep && dep._flags & SubscriberFlagsEffect && dep._flags & SubscriberFlagsPropagated) {
          notifyEffect(dep);
        }
        link = link.nextDep;
      } while (link !== undefined);
    }
  }

  /**
   * Processes queued effect notifications after a batch operation finishes.
   *
   * Iterates through all queued effects, calling notifyEffect on each. If an
   * effect remains partially handled, its flags are updated, and future
   * notifications may be triggered until fully handled.
   */
  function processEffectNotifications() {
    while (notifyIndex < notifyBufferLength) {
      const effect = notifyBuffer[notifyIndex];
      notifyBuffer[notifyIndex++] = undefined;
      if (!notifyEffect(effect)) {
        effect._flags &= ~SubscriberFlagsNotified;
      }
    }
    notifyIndex = 0;
    notifyBufferLength = 0;
  }

  /**
   * Creates and attaches a new link between the given dependency and
   * subscriber.
   *
   * Reuses a link object from the linkPool if available. The newly formed link
   * is added to both the dependency's linked list and the subscriber's linked
   * list.
   *
   * @param dep - The dependency to link.
   * @param sub - The subscriber to be attached to this dependency.
   * @param nextDep - The next link in the subscriber's chain.
   * @param depsTail - The current tail link in the subscriber's chain.
   * @returns The newly created link object.
   */
  function linkNewDep(dep, sub, nextDep, depsTail) {
    const newLink = {
      dep,
      sub,
      nextDep,
      prevSub: undefined,
      nextSub: undefined
    };
    if (depsTail === undefined) {
      sub._deps = newLink;
    } else {
      depsTail.nextDep = newLink;
    }
    if (dep._subs === undefined) {
      dep._subs = newLink;
    } else {
      const oldTail = dep._subsTail;
      newLink.prevSub = oldTail;
      oldTail.nextSub = newLink;
    }
    sub._depsTail = newLink;
    dep._subsTail = newLink;
    return newLink;
  }

  /**
   * Recursively checks and updates all computed subscribers marked as pending.
   *
   * It traverses the linked structure using a stack mechanism. For each
   * computed subscriber in a pending state, updateComputed is called and
   * shallowPropagate is triggered if a value changes. Returns whether any
   * updates occurred.
   *
   * @param current - The starting link representing a sequence of pending
   *   computeds.
   * @returns `true` if a computed was updated, otherwise `false`.
   */
  function checkDirty(current) {
    let prevLinks;
    let checkDepth = 0;
    let dirty;
    top: do {
      dirty = false;
      const dep = current.dep;
      if (current.sub._flags & SubscriberFlagsDirty) {
        dirty = true;
      } else if ('_flags' in dep) {
        const depFlags = dep._flags;
        if ((depFlags & (SubscriberFlagsComputed | SubscriberFlagsDirty)) === (SubscriberFlagsComputed | SubscriberFlagsDirty)) {
          if (updateComputed(dep)) {
            const subs = dep._subs;
            if (subs.nextSub !== undefined) {
              shallowPropagate(subs);
            }
            dirty = true;
          }
        } else if ((depFlags & (SubscriberFlagsComputed | SubscriberFlagsPendingComputed)) === (SubscriberFlagsComputed | SubscriberFlagsPendingComputed)) {
          if (current.nextSub !== undefined || current.prevSub !== undefined) {
            prevLinks = {
              target: current,
              linked: prevLinks
            };
          }
          current = dep._deps;
          ++checkDepth;
          continue;
        }
      }
      if (!dirty && current.nextDep !== undefined) {
        current = current.nextDep;
        continue;
      }
      while (checkDepth) {
        --checkDepth;
        const sub = current.sub;
        const firstSub = sub._subs;
        if (dirty) {
          if (updateComputed(sub)) {
            if (firstSub.nextSub !== undefined) {
              current = prevLinks.target;
              prevLinks = prevLinks.linked;
              shallowPropagate(firstSub);
            } else {
              current = firstSub;
            }
            continue;
          }
        } else {
          sub._flags &= ~SubscriberFlagsPendingComputed;
        }
        if (firstSub.nextSub !== undefined) {
          current = prevLinks.target;
          prevLinks = prevLinks.linked;
        } else {
          current = firstSub;
        }
        if (current.nextDep !== undefined) {
          current = current.nextDep;
          continue top;
        }
        dirty = false;
      }
      return dirty;
      // eslint-disable-next-line
    } while (true);
  }

  /**
   * Quickly propagates PendingComputed status to Dirty for each subscriber in
   * the chain.
   *
   * If the subscriber is also marked as an effect, it is added to the
   * queuedEffects list for later processing.
   *
   * @param link - The head of the linked list to process.
   */
  function shallowPropagate(link) {
    do {
      const sub = link.sub;
      const subFlags = sub._flags;
      if ((subFlags & (SubscriberFlagsPendingComputed | SubscriberFlagsDirty)) === SubscriberFlagsPendingComputed) {
        sub._flags = subFlags | SubscriberFlagsDirty | SubscriberFlagsNotified;
        if ((subFlags & (SubscriberFlagsEffect | SubscriberFlagsNotified)) === SubscriberFlagsEffect) {
          notifyBuffer[notifyBufferLength++] = sub;
        }
      }
      link = link.nextSub;
    } while (link !== undefined);
  }

  /**
   * Verifies whether the given link is valid for the specified subscriber.
   *
   * It iterates through the subscriber's link list (from sub.deps to
   * sub.depsTail) to determine if the provided link object is part of that
   * chain.
   *
   * @param checkLink - The link object to validate.
   * @param sub - The subscriber whose link list is being checked.
   * @returns `true` if the link is found in the subscriber's list; otherwise
   *   `false`.
   */
  function isValidLink(checkLink, sub) {
    const depsTail = sub._depsTail;
    if (depsTail !== undefined) {
      let link = sub._deps;
      do {
        if (link === checkLink) {
          return true;
        }
        if (link === depsTail) {
          break;
        }
        link = link.nextDep;
      } while (link !== undefined);
    }
    return false;
  }

  /**
   * Clears dependency-subscription relationships starting at the given link.
   *
   * Detaches the link from both the dependency and subscriber, then continues
   * to the next link in the chain. The link objects are returned to linkPool
   * for reuse.
   *
   * @param link - The head of a linked chain to be cleared.
   */
  function clearTracking(link) {
    do {
      const dep = link.dep;
      const nextDep = link.nextDep;
      const nextSub = link.nextSub;
      const prevSub = link.prevSub;
      if (nextSub !== undefined) {
        nextSub.prevSub = prevSub;
      } else {
        dep._subsTail = prevSub;
      }
      if (prevSub !== undefined) {
        prevSub.nextSub = nextSub;
      } else {
        dep._subs = nextSub;
      }
      if (dep._subs === undefined && '_deps' in dep) {
        const depFlags = dep._flags;
        if (!(depFlags & SubscriberFlagsDirty)) {
          dep._flags = depFlags | SubscriberFlagsDirty;
        }
        const depDeps = dep._deps;
        if (depDeps !== undefined) {
          link = depDeps;
          dep._depsTail.nextDep = nextDep;
          dep._deps = undefined;
          dep._depsTail = undefined;
          continue;
        }
      }
      link = nextDep;
    } while (link !== undefined);
  }
}

function toObserver(nextHandler, errorHandler, completionHandler) {
  const isObserver = typeof nextHandler === 'object';
  const self = isObserver ? nextHandler : undefined;
  return {
    next: (isObserver ? nextHandler.next : nextHandler)?.bind(self),
    error: (isObserver ? nextHandler.error : errorHandler)?.bind(self),
    complete: (isObserver ? nextHandler.complete : completionHandler)?.bind(self)
  };
}

const [link, propagate, updateDirtyFlag, startTracking, endTracking, processEffectNotifications, processComputedUpdate] = createReactiveSystem({
  updateComputed(computed) {
    return computed._update();
  },
  notifyEffect(effect) {
    effect.notify();
    return true;
  }
});
let activeSub = undefined;
function createAtom(valueOrFn, options) {
  const isComputed = typeof valueOrFn === 'function';
  const getter = valueOrFn;

  // Create plain object atom
  const atom = {
    _snapshot: isComputed ? undefined : valueOrFn,
    // Dependency fields
    _subs: undefined,
    _subsTail: undefined,
    get() {
      if (activeSub !== undefined) {
        link(atom, activeSub);
      }
      return atom._snapshot;
    },
    subscribe(observerOrFn) {
      const obs = toObserver(observerOrFn);
      const observed = {
        current: false
      };
      const e = effect(() => {
        atom.get();
        if (!observed.current) {
          observed.current = true;
        } else {
          obs.next?.(atom._snapshot);
        }
      });
      return {
        unsubscribe: () => {
          e.stop();
        }
      };
    }
  };
  if (isComputed) {
    Object.assign(atom, {
      _deps: undefined,
      _depsTail: undefined,
      _flags: SubscriberFlagsComputed | SubscriberFlagsDirty,
      get() {
        const flags = this._flags;
        if (flags & (SubscriberFlagsPendingComputed | SubscriberFlagsDirty)) {
          processComputedUpdate(atom, flags);
        }
        if (activeSub !== undefined) {
          link(atom, activeSub);
        }
        return atom._snapshot;
      },
      _update() {
        const prevSub = activeSub;
        const compare = options?.compare ?? Object.is;
        activeSub = atom;
        startTracking(atom);
        try {
          const oldValue = atom._snapshot;
          const read = atom => atom.get();
          const newValue = getter(read);
          if (oldValue === undefined || !compare(oldValue, newValue)) {
            atom._snapshot = newValue;
            return true;
          }
          return false;
        } finally {
          activeSub = prevSub;
          endTracking(atom);
        }
      }
    });
  } else {
    Object.assign(atom, {
      set(valueOrFn) {
        const compare = options?.compare ?? Object.is;
        const value = typeof valueOrFn === 'function' ? valueOrFn(atom._snapshot) : valueOrFn;
        if (compare(atom._snapshot, value)) return;
        atom._snapshot = value;
        const {
          _subs: subs
        } = atom;
        if (subs !== undefined) {
          propagate(subs);
          processEffectNotifications();
        }
      }
    });
  }
  return atom;
}
function effect(fn) {
  const run = () => {
    const prevSub = activeSub;
    activeSub = effectObj;
    startTracking(effectObj);
    try {
      return fn();
    } finally {
      activeSub = prevSub;
      endTracking(effectObj);
    }
  };
  const effectObj = {
    // Subscriber fields
    _deps: undefined,
    _depsTail: undefined,
    _flags: SubscriberFlagsEffect,
    notify() {
      const flags = this._flags;
      if (flags & SubscriberFlagsDirty || flags & SubscriberFlagsPendingComputed && updateDirtyFlag(this, flags)) {
        run();
      }
    },
    stop() {
      startTracking(this);
      endTracking(this);
    }
  };
  run();
  return effectObj;
}

const symbolObservable = (() => typeof Symbol === 'function' && Symbol.observable || '@@observable')();

/**
 * Updates a context object using a recipe function.
 *
 * @param context - The current context
 * @param recipe - A function that describes how to update the context
 * @returns The updated context
 */
function setter(context, recipe) {
  return recipe(context);
}
const inspectionObservers = new WeakMap();
function createStoreCore(initialContext, transitions, emits,
// TODO: improve this type
producer) {
  let listeners;
  const initialSnapshot = {
    context: initialContext,
    status: 'active',
    output: undefined,
    error: undefined
  };
  let currentSnapshot = initialSnapshot;
  const atom = createAtom(currentSnapshot);
  const emit = ev => {
    if (!listeners) {
      return;
    }
    const type = ev.type;
    const typeListeners = listeners.get(type);
    if (typeListeners) {
      typeListeners.forEach(listener => listener(ev));
    }
  };
  const transition = createStoreTransition(transitions, producer);
  function receive(event) {
    let effects;
    [currentSnapshot, effects] = transition(currentSnapshot, event);
    inspectionObservers.get(store)?.forEach(observer => {
      observer.next?.({
        type: '@xstate.snapshot',
        event,
        snapshot: currentSnapshot,
        actorRef: store,
        rootId: store.sessionId
      });
    });
    atom.set(currentSnapshot);
    for (const effect of effects) {
      if (typeof effect === 'function') {
        effect();
      } else {
        // handle the inherent effect first
        emits?.[effect.type]?.(effect);
        emit(effect);
      }
    }
  }
  const store = {
    get _snapshot() {
      return atom._snapshot;
    },
    on(emittedEventType, handler) {
      if (!listeners) {
        listeners = new Map();
      }
      let eventListeners = listeners.get(emittedEventType);
      if (!eventListeners) {
        eventListeners = new Set();
        listeners.set(emittedEventType, eventListeners);
      }
      const wrappedHandler = handler.bind(undefined);
      eventListeners.add(wrappedHandler);
      return {
        unsubscribe() {
          eventListeners.delete(wrappedHandler);
        }
      };
    },
    transition,
    sessionId: uniqueId(),
    send(event) {
      inspectionObservers.get(store)?.forEach(observer => {
        observer.next?.({
          type: '@xstate.event',
          event,
          sourceRef: undefined,
          actorRef: store,
          rootId: store.sessionId
        });
      });
      receive(event);
    },
    getSnapshot() {
      return currentSnapshot;
    },
    get() {
      return atom.get();
    },
    getInitialSnapshot() {
      return initialSnapshot;
    },
    subscribe: atom.subscribe.bind(atom),
    [symbolObservable]() {
      return this;
    },
    inspect: observerOrFn => {
      const observer = toObserver(observerOrFn);
      inspectionObservers.set(store, inspectionObservers.get(store) ?? new Set());
      inspectionObservers.get(store).add(observer);
      observer.next?.({
        type: '@xstate.actor',
        actorRef: store,
        rootId: store.sessionId
      });
      observer.next?.({
        type: '@xstate.snapshot',
        snapshot: initialSnapshot,
        event: {
          type: '@xstate.init'
        },
        actorRef: store,
        rootId: store.sessionId
      });
      return {
        unsubscribe() {
          return inspectionObservers.get(store)?.delete(observer);
        }
      };
    },
    trigger: new Proxy({}, {
      get: (_, eventType) => {
        return payload => {
          store.send({
            type: eventType,
            ...payload
          });
        };
      }
    }),
    select(selector, equalityFn = Object.is) {
      return createAtom(() => selector(store.get().context), {
        compare: equalityFn
      });
    }
  };
  return store;
}
/**
 * Creates a **store** that has its own internal state and can be sent events
 * that update its internal state based on transitions.
 *
 * @example
 *
 * ```ts
 * const store = createStore({
 *   context: { count: 0 },
 *   on: {
 *     inc: (context, event: { by: number }) => ({
 *       count: context.count + event.by
 *     })
 *   }
 * });
 *
 * store.subscribe((snapshot) => {
 *   console.log(snapshot);
 * });
 *
 * store.send({ type: 'inc', by: 5 });
 * // Logs { context: { count: 5 }, status: 'active', ... }
 * ```
 *
 * @param config - The store configuration object
 * @param config.context - The initial state of the store
 * @param config.on - An object mapping event types to transition functions
 * @param config.emits - An object mapping emitted event types to handlers
 * @returns A store instance with methods to send events and subscribe to state
 *   changes
 */
function _createStore(...[{
  context,
  on,
  emits
}]) {
  return createStoreCore(context, on, emits);
}

// those overloads are exactly the same, we only duplicate them so TypeScript can:
// 1. assign contextual parameter types during inference attempt for the first overload when the source object is still context-sensitive and often non-inferrable
// 2. infer correctly during inference attempt for the second overload when the parameter types are already "known"
const createStore = _createStore;
function _createStoreConfig(definition) {
  return definition;
}
const createStoreConfig = _createStoreConfig;

/**
 * Creates a `Store` with a provided producer (such as Immer's `producer(…)` A
 * store has its own internal state and can receive events.
 *
 * @example
 *
 * ```ts
 * import { produce } from 'immer';
 *
 * const store = createStoreWithProducer(produce, {
 *   context: { count: 0 },
 *   on: {
 *     inc: (context, event: { by: number }) => {
 *       context.count += event.by;
 *     }
 *   }
 * });
 *
 * store.subscribe((snapshot) => {
 *   console.log(snapshot);
 * });
 *
 * store.send({ type: 'inc', by: 5 });
 * // Logs { context: { count: 5 }, status: 'active', ... }
 * ```
 */
function createStoreWithProducer(producer, config) {
  return createStoreCore(config.context, config.on, config.emits, producer);
}
/**
 * Creates a store transition function that handles state updates based on
 * events.
 *
 * @param transitions - An object mapping event types to transition functions
 * @param producer - Optional producer function (e.g., Immer's produce) for
 *   immutable updates
 * @returns A transition function that takes a snapshot and event and returns a
 *   new snapshot with effects
 */
function createStoreTransition(transitions, producer) {
  return (snapshot, event) => {
    let currentContext = snapshot.context;
    const assigner = transitions?.[event.type];
    const effects = [];
    const enqueue = {
      emit: new Proxy({}, {
        get: (_, eventType) => {
          return payload => {
            effects.push({
              ...payload,
              type: eventType
            });
          };
        }
      }),
      effect: fn => {
        effects.push(fn);
      }
    };
    if (!assigner) {
      return [snapshot, effects];
    }
    if (typeof assigner === 'function') {
      currentContext = producer ? producer(currentContext, draftContext => assigner(draftContext, event, enqueue)) : setter(currentContext, draftContext => Object.assign({}, currentContext, assigner?.(draftContext, event,
      // TODO: help me
      enqueue)));
    } else {
      const partialUpdate = {};
      for (const key of Object.keys(assigner)) {
        const propAssignment = assigner[key];
        partialUpdate[key] = typeof propAssignment === 'function' ? propAssignment(currentContext, event, enqueue) : propAssignment;
      }
      currentContext = Object.assign({}, currentContext, partialUpdate);
    }
    return [{
      ...snapshot,
      context: currentContext
    }, effects];
  };
}

/**
 * Generates a unique 6-character identifier.
 *
 * @returns A random string identifier
 */
function uniqueId() {
  return Math.random().toString(36).slice(6);
}

export { createStore as a, createStoreWithProducer as b, createStoreTransition as c, createStoreConfig as d, createAtom as e };
